import { AbstractModel } from '@/models/base';
import { IContract, IContractData } from './contract.types';
import { IContractItem, IContractItemData } from './contract.types';
import { IContractAttachment, IContractAttachmentData } from './contract.types';

import {
  CONTRACT_DEFAULT_GRACE_PERIOD,
  CONTRACT_THIRTY_DAY_RENEWAL_DATE_RANGE,
  CONTRACT_SIXTY_DAY_RENEWAL_DATE_RANGE,
  ContractPhaseEnum,
  ContractStateEnum,
  ContractRenewalStateEnum
} from './contract.constants';
import { IQuote, Quote } from '../quote';
import {
  IQuotePaymentTermCode,
  QuotePaymentTermCode
} from '../quote-payment-term-code';
import { Currency, ICurrency } from '../currency';
import moment from 'moment-timezone';
import { padStart, startCase } from 'lodash';
import { IFieldErrorsData, IFieldErrors } from '../error/error.types';
import { FieldErrors } from '../error/error';

export class Contract extends AbstractModel implements IContract {
  readonly id?: number;
  readonly quote: IQuote | number;
  readonly grace_period_days: number = CONTRACT_DEFAULT_GRACE_PERIOD;
  readonly csi_order_number: string = '';
  readonly customer_account_number: string;
  readonly payment_term: IQuotePaymentTermCode | number | null = null;
  readonly total_value: number = 0;
  readonly files: IContractAttachment[] = [];
  readonly currency: number | ICurrency;

  readonly state: ContractStateEnum = ContractStateEnum.Draft;
  readonly phase: ContractPhaseEnum = ContractPhaseEnum.Pending;
  readonly renewal_state: ContractRenewalStateEnum =
    ContractRenewalStateEnum.NotStarted;

  readonly renewal_required: boolean = false;
  readonly is_renewal: boolean = false;

  readonly activation_date: string | null = null;
  readonly expiry_date: string | null = null;

  readonly items: IContractItem[] = [];

  constructor(data: IContractData) {
    super();

    const requiredFields: (keyof IContractData)[] = [
      'quote',
      'customer_account_number',
      'currency'
    ];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.csi_order_number = data.csi_order_number!;
    this.customer_account_number = data.customer_account_number!;
    this.quote =
      typeof data.quote === 'number' ? data.quote : new Quote(data.quote!);

    this.state = data.state ?? ContractStateEnum.Draft;
    this.phase = data.phase ?? ContractPhaseEnum.Pending;
    this.renewal_state =
      data.renewal_state ?? ContractRenewalStateEnum.NotStarted;

    if (data.renewal_required) {
      this.renewal_required = data.renewal_required;
    }

    if (data.is_renewal) {
      this.is_renewal = data.is_renewal;
    }

    if (data.payment_term) {
      this.payment_term =
        typeof data.payment_term === 'number'
          ? data.payment_term
          : new QuotePaymentTermCode(data.payment_term);
    }

    this.currency =
      typeof data.currency === 'number'
        ? data.currency
        : new Currency(data.currency!);

    if (data.grace_period_days) {
      this.grace_period_days = data.grace_period_days;
    }

    if (data.activation_date) {
      this.activation_date = data.activation_date;
    }
    if (data.expiry_date) {
      this.expiry_date = data.expiry_date;
    }
    if (data.total_value) {
      this.total_value = data.total_value;
    }
    if (data.files) {
      this.files = data.files.map(file => new ContractAttachment(file));
    }

    if (data.items) {
      this.items = data.items.map(item => new ContractItem(item));
    }
  }

  get contractId() {
    if (!this.id) return '';
    // prepend fours '0's to initial id string length 'i.e: 00001'
    return padStart(this.id.toString(), 5, '0');
  }

  toString() {
    let str = `${this.contractId}`;

    if (this.quote instanceof Quote && !!this.quote.customer) {
      str += ` - ${this.quote.customer.name}`;
    }

    return str;
  }

  getFieldErrors(): IFieldErrors {
    const invalidFields: IFieldErrorsData = {};

    const requiredFields: (keyof IContractData)[] = [
      'quote',
      'customer_account_number',
      'csi_order_number',
      'currency',
      'payment_term',
      'activation_date',
      'expiry_date',
      'items'
    ];
    for (const field of requiredFields) {
      const value = this[field];

      if (value === null || (typeof value === 'string' && value == '')) {
        invalidFields[field] = [`${startCase(field)} is required`];
      }

      if (Array.isArray(value) && value.length === 0) {
        invalidFields[field] = [`${startCase(field)} are required`];
      }
    }
    return new FieldErrors(invalidFields);
  }

  isValid(): boolean {
    return Object.keys(this.getFieldErrors()).length === 0;
  }

  isExpired(): boolean {
    return moment(new Date()).isAfter(this.expiry_date, 'day');
  }

  canRenew(): boolean {
    return this.renewal_required;
  }

  isInThirtyDayDurationThreshold(date = new Date()) {
    return this.daysTillExpiryDate(
      date,
      CONTRACT_THIRTY_DAY_RENEWAL_DATE_RANGE
    );
  }

  expiryDateMessage(): string {
    if (this.isExpired()) {
      return `Contract has expired ${moment(this.expiry_date).from(
        new Date()
      )}`;
    }

    // LF-2024-12-02 - Temporary hack until LeRon is back to fix properly
    const now = moment();
    const expiryMoment = moment(this.expiry_date);
    expiryMoment.set({
      hour: now.hour(),
      minute: now.minute(),
      second: now.second(),
      millisecond: now.millisecond()
    });

    return `Contract expires in ${moment
      .duration(expiryMoment.diff(now))
      .asDays()
      .toFixed(0)} days on ${moment(this.expiry_date).format('LL')}`;
  }

  private daysTillExpiryDate(date: Date, dayDuration: number): boolean {
    if (!this.expiry_date) return false;

    return (
      moment
        .duration({
          from: date,
          to: this.expiry_date
        })
        .asDays() < dayDuration
    );
  }

  getStatusColor(): string {
    switch (this.state) {
      case ContractStateEnum.Draft:
        return 'draft';

      case ContractStateEnum.Terminated:
        return 'cancel';

      case ContractStateEnum.Executed:
        return 'approved';

      default:
        return 'primary';
    }
  }

  getStatusMessage(): string {
    switch (this.state) {
      case ContractStateEnum.Draft:
        return 'Draft';

      case ContractStateEnum.Executed:
        return 'Executed';

      case ContractStateEnum.Terminated:
        return 'Terminated';

      default:
        return 'N/A';
    }
  }

  getPhaseColor(): string {
    switch (this.phase) {
      case ContractPhaseEnum.Draft:
        return 'draft';

      case ContractPhaseEnum.Pending:
        return 'submitted';

      case ContractPhaseEnum.Active:
        return 'green';

      case ContractPhaseEnum.Expired:
        return 'error';

      case ContractPhaseEnum.Terminated:
        return 'cancel';

      default:
        return 'primary';
    }
  }

  getPhaseMessage(): string {
    switch (this.phase) {
      case ContractPhaseEnum.Draft:
        return 'Draft';

      case ContractPhaseEnum.Pending:
        return 'Pending';

      case ContractPhaseEnum.Active:
        return 'Active';

      case ContractPhaseEnum.Expired:
        return 'Expired';

      case ContractPhaseEnum.Terminated:
        return 'Terminated';

      default:
        return 'N/A';
    }
  }

  getRenewalStateColor(): string {
    switch (this.renewal_state) {
      case ContractRenewalStateEnum.AwaitingConfirmation:
        return 'orange';

      case ContractRenewalStateEnum.ConfirmedWithChanges:
      case ContractRenewalStateEnum.ConfirmedWithoutChanges:
        return 'primary';

      case ContractRenewalStateEnum.Declined:
        return 'reject';

      case ContractRenewalStateEnum.NotStarted:
      default:
        return 'draft';
    }
  }

  getRenewalStateMessage(): string {
    switch (this.renewal_state) {
      case ContractRenewalStateEnum.AwaitingConfirmation:
        return 'Awaiting Confirmation';

      case ContractRenewalStateEnum.ConfirmedWithChanges:
      case ContractRenewalStateEnum.ConfirmedWithoutChanges:
        return 'Renewal Confirmed';

      case ContractRenewalStateEnum.Declined:
        return 'Renewal Declined';

      case ContractRenewalStateEnum.NotStarted:
      default:
        return 'Not Started';
    }
  }

  hasRenewalProcessStarted(): boolean {
    return (
      [
        ContractRenewalStateEnum.AwaitingConfirmation,
        ContractRenewalStateEnum.Declined
      ].includes(this.renewal_state) || this.isContractRenewed()
    );
  }

  isContractRenewed() {
    return [
      ContractRenewalStateEnum.ConfirmedWithChanges,
      ContractRenewalStateEnum.ConfirmedWithoutChanges
    ].includes(this.renewal_state);
  }
}

export class ContractItem extends AbstractModel implements IContractItem {
  readonly id?: number | undefined;
  readonly item: string;
  readonly quantity: number = 0;
  readonly price: number = 0;
  readonly activation_date: string | null = null;
  readonly net_price: number = 0;

  constructor(data: IContractItemData) {
    super();

    const requiredFields: (keyof IContractItemData)[] = ['item'];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.item = data.item!;

    if (data.price) {
      this.price = data.price;
    }
    if (data.net_price) {
      this.net_price = data.net_price;
    }
    if (data.quantity) {
      this.quantity = data.quantity;
    }
    if (data.activation_date) {
      this.activation_date = data.activation_date;
    }
  }

  toString() {
    return this.item;
  }
}

export class ContractAttachment extends AbstractModel
  implements IContractAttachment {
  readonly id?: number | undefined;
  readonly file: string | File;
  readonly filename: string = '';

  constructor(data: IContractAttachmentData) {
    super();

    const requiredFields: (keyof IContractAttachmentData)[] = ['file'];
    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    if (data.filename) {
      this.filename = data.filename;
    }

    this.file = data.file!;
  }

  toString() {
    return this.filename;
  }
}
